import {HeaderAgentTasks} from '@github-ui/issue-agent-tasks/HeaderAgentTasks'
import {Suspense} from 'react'

import type {OptionConfig} from '../OptionConfig'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import {SmallHeaderIssueType} from './HeaderIssueType'
import {HeaderSubIssueSummary, type HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import {LinkedSecurityAlerts} from './LinkedSecurityAlerts'
import {SmallRepositoryPill} from './RepositoryPill'
import type {
  StickyHeaderIssueNode,
  StickyHeaderRepositoryNode,
  StickyHeaderRepositoryOwnerNode,
} from './sticky-header-query-types'
import styles from './StickyHeaderChips.module.css'
import {SmallTrackedBy, type TrackedByFragment} from './TrackedBy'

export type StickyHeaderChipsFragment = Pick<
  StickyHeaderIssueNode,
  'url' | 'subIssuesSummary' | 'agentAssignments' | 'closedByPullRequestsReferences'
> & {
  readonly repository: Pick<StickyHeaderRepositoryNode, 'nameWithOwner' | 'name' | 'isPrivate'> & {
    readonly owner: Pick<StickyHeaderRepositoryOwnerNode, 'login' | '__typename'> & {
      readonly name?: string | null
    }
  }
  readonly issueType: {readonly name: string; readonly color: string} | null
}

export type StickyHeaderChipsProps = {
  issueData: StickyHeaderChipsFragment
  /** Read from the header refetch, so the blocked-by summary follows the live state. */
  state?: HeaderBlockedBySummaryFragment['state']
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  optionConfig: OptionConfig
}

export function StickyHeaderChips({
  issueData: data,
  state,
  linkedSecurityAlertsData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  optionConfig,
}: StickyHeaderChipsProps) {
  const {showRepositoryPill} = optionConfig

  return (
    <div className={styles.chipsRow}>
      <SmallHeaderIssueType data={data} />
      <HeaderAgentTasks issueData={data} isSticky />
      <HeaderSubIssueSummary subIssuePrimaryKey={data} subIssueSecondaryKey={subIssueSecondaryData} size="small" />
      {linkedSecurityAlertsData && (
        <>
          <HeaderBlockedBySummary state={state} size="small" repositoryNameWithOwner={data.repository.nameWithOwner} />
          <LinkedSecurityAlerts issueKey={linkedSecurityAlertsData} isSmall />
          <Suspense>
            <SmallTrackedBy url={data.url} trackedByData={trackedBySecondaryData} />
          </Suspense>
        </>
      )}
      {showRepositoryPill && (
        <SmallRepositoryPill
          repositoryPillData={data}
          isAccountIssue={optionConfig.accountIssueOption?.isAccountIssue}
        />
      )}
    </div>
  )
}
